import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import TimePicker from "@components/ui/selection/TimePicker";
import DatePicker from "@components/ui/selection/DatePicker";
import CheckBox from "@components/ui/selection/CheckBox";
import Icon1 from "@assets/icons/Icon";
import { useMemo, useState } from "react";
import { useDataStore } from "@store/user_org";
import { usePlanningStore, getCategories } from "@store/planningStore";
import { appointmentsApi } from "@services/api/appointments";
import { showNotification } from "context/notification.service";
import { hourToTimeStr, timeStrToHour, formatTime, GRID_HOUR_START, GRID_HOUR_END, isoToSeconds, secondsToIso } from "@utils/timeGrid";
import { getReceptionKind } from "types/org";
import type { Appointment, AppointmentCategory } from "types/garage/planning";

interface InternalAppointmentModalProps {
  appointment: Appointment;
  onClose: () => void;
  onSaved?: (updated: Appointment) => void;
  onDeleted?: () => void;
}

export default function InternalAppointmentModal({
  appointment,
  onClose,
  onSaved,
  onDeleted,
}: InternalAppointmentModalProps) {
  const orgData = useDataStore((s) => s.orgData);
  const [title, setTitle] = useState(appointment.clientName || "");
  const [category, setCategory] = useState<AppointmentCategory>(appointment.category);
  const [startHour, setStartHour] = useState<number>(appointment.startHour);
  const [endHour, setEndHour] = useState<number>(appointment.startHour + appointment.durationHours);
  const [allDay, setAllDay] = useState<boolean>(!!appointment.allDay);
  const [endDate, setEndDate] = useState<string>(appointment.endDate || appointment.date);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const allCategories = useMemo(() => getCategories(orgData), [orgData]);
  const allReceptions = useMemo(() => orgData?.receptions ?? [], [orgData?.receptions]);
  const compatibleCategories = useMemo(() => {
    return allCategories.filter((c) => {
      const rec = allReceptions.find((r) => r.id === c.id);
      const k = rec ? getReceptionKind(rec) : "reception";
      return k === "divers" || k === "pret";
    });
  }, [allCategories, allReceptions]);

  const refreshPlanning = () => {
    const planningState = usePlanningStore.getState();
    if (planningState._fetchedOrgId) {
      planningState.fetchAppointments(planningState._fetchedOrgId, true);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const effectiveEndDate = allDay && endDate > appointment.date ? endDate : appointment.date;
      const updated = await appointmentsApi.update(appointment.id, {
        clientName: title.trim(),
        category,
        allDay,
        endDate: effectiveEndDate,
        startHour: allDay ? GRID_HOUR_START : startHour,
        durationHours: allDay
          ? GRID_HOUR_END - GRID_HOUR_START
          : Math.max(0.25, endHour - startHour),
      });
      refreshPlanning();
      showNotification({ message: "Bloc mis à jour", type: "success" });
      onSaved?.(updated);
      onClose();
    } catch {
      showNotification({ message: "Erreur lors de la sauvegarde", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await appointmentsApi.delete(appointment.id);
      refreshPlanning();
      showNotification({ message: "Bloc supprimé", type: "success" });
      onDeleted?.();
      onClose();
    } catch {
      showNotification({ message: "Erreur lors de la suppression", type: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const dateObj = new Date(appointment.date + "T00:00:00");
  const dateLabel = dateObj.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
  });

  return (
    <BaseModal
      title="Bloc interne"
      startIcon={<Icon1 name="horloge" size={20} />}
      onClose={onClose}
      width="max-w-md"
    >
      <div className="flex flex-col gap-3 p-4">
        <div className="flex flex-row items-center gap-2 p-3 bg-indigo-50 rounded-lg">
          <Icon1 name="horloge" size={16} color="#6366F1" />
          <span className="text-h3 font-medium text-indigo-700">
            {allDay
              ? endDate > appointment.date
                ? `Du ${dateLabel} au ${new Date(endDate + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" })}`
                : `${dateLabel} — journée complète`
              : `${dateLabel} — ${formatTime(startHour)}`}
          </span>
        </div>

        <TextField
          label="Motif"
          value={title}
          onChange={setTitle}
          placeholder="Réunion, formation, pause, absence..."
        />

        <label className="flex flex-row items-center gap-2 cursor-pointer select-none">
          <CheckBox
            checked={allDay}
            onChange={(e) => {
              const checked = e.target.checked;
              setAllDay(checked);
              if (checked && endDate < appointment.date) setEndDate(appointment.date);
            }}
          />
          <span className="text-h3 text-neutral-700">Journée complète</span>
        </label>

        {allDay ? (
          <DatePicker
            label="Jusqu'au (inclus)"
            seconds={isoToSeconds(endDate)}
            minDateSeconds={isoToSeconds(appointment.date)}
            onChange={(s) => {
              if (!s) return;
              setEndDate(secondsToIso(s));
            }}
          />
        ) : (
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <TimePicker
                label="Heure de début"
                value={hourToTimeStr(startHour)}
                onChange={(t) => {
                  if (!t) return;
                  const h = timeStrToHour(t);
                  setStartHour(h);
                  if (h >= endHour) setEndHour(Math.min(h + 1, GRID_HOUR_END));
                }}
              />
            </div>
            <div className="flex-1">
              <TimePicker
                label="Heure de fin"
                value={hourToTimeStr(endHour)}
                onChange={(t) => {
                  if (!t) return;
                  setEndHour(timeStrToHour(t));
                }}
              />
            </div>
          </div>
        )}

        <DropDown
          label="Catégorie"
          options={compatibleCategories.map((c) => ({ value: c.id, label: c.label }))}
          value={category}
          onSelect={(v) => setCategory(v as AppointmentCategory)}
        />
      </div>

      <div className="border-t p-4 flex flex-row justify-between gap-2">
        <SecondaryButton variant="small" danger onClick={handleDelete} disabled={deleting || saving}>
          {deleting ? "Suppression..." : "Supprimer"}
        </SecondaryButton>
        <div className="flex flex-row gap-2">
          <SecondaryButton variant="small" onClick={onClose}>
            Fermer
          </SecondaryButton>
          <Button
            variant="small"
            label={saving ? "Enregistrement..." : "Enregistrer"}
            onClick={handleSave}
            disabled={saving || deleting || !title.trim()}
          />
        </div>
      </div>
    </BaseModal>
  );
}
